import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import React, { useState, useReducer, useEffect } from 'react';
import { BaseReact } from '../../base.model';
import { Receptor } from '../../models/Catalogos';
import { Bill, BillConceptos, CFDI, FiscalpopCFDI, ToBillArguments, ToBillEvent } from '../../models/Factura';
import { ProfileQL } from '../../models/Profile';
import SectionTitleBar from '../components/titlebar';

import { graphqlSchema } from '../../services/graphql.schema';

import './CreditNote.scss'

import FacturarConceptosDevolucion from '../Facturar/conceptos/Factura.devolucion';
import { useParams } from 'react-router-dom';
import FacturaTotales from '../Facturar/components/Factura.totales';
import { calculateTotal } from '../../utils/calculate.conceptos';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { validateNotaCredIsComplete } from '../../services/validation.cfdi';
import CreditNotesClientDialog from './clientModal/creditNotes.clientEdit';
import CreditNoteRelated from './components/CreditNote.related';
import CreditNoteActions from './components/CreditNote.actions';
import { parseStampErrorFromGraphQL } from '../../utils/error.graphql';
import { billRelatedReducer, billReducer, EMPTY_CFDI, INITIAL_CFDI } from './handlers/creditNote.state';
import { useReceptorSearch } from '../Hooks/useReceptorSearch';

interface CreditNoteProps extends BaseReact {
    currentUser: ProfileQL;
}

function CreditNote({ currentUser, location, history }: CreditNoteProps) {
    const { eventid } = useParams<{ eventid: string }>();

    const [validRequested, setValidRequested] = useState(false);
    const [stamping, setStamping] = useState(false);

    const [billRelates, setBillRelates] = useState<FiscalpopCFDI[]>([]);
    const { receptorOptions, searchReceptorByName, searchReceptorByRfc } = useReceptorSearch({ initialSearch: '' });

    const [confirmedRelated, setConfirmRelated] = useState(false);
    const [receptorEditDialog, setReceptorEditDialog] = useState(false);

    const [cfdi, dispatchBill] = useReducer(billReducer, INITIAL_CFDI());
    const [cfdiRelatedOptions, dispatchCfdiRelated] = useReducer(billRelatedReducer, [])

    const [submitNota] = useMutation(graphqlSchema.FISCALPOP.CFDI.stampFromNotaCredito, {
        onCompleted: ({ stampFromNotaCredito }: { stampFromNotaCredito: ToBillArguments<ToBillEvent> }) => {
            sharedToasterSubject.next({ type: 'confirm', message: 'Nota de crédito emitida correctamente' });
            history.push({
                pathname: '/',
                state: { billArgument: stampFromNotaCredito }
            })
        },
        onError: (e) => {
            console.error('Error submitting nota');
            setStamping(false)
            sharedToasterSubject.next({ type: 'error', message: parseStampErrorFromGraphQL(e.graphQLErrors[0]), clearTs: 5200, clickToClose: true })
        }
    })
    const [submitNotaToEdit] = useMutation(graphqlSchema.FISCALPOP.CFDI.restampBillNotaCredito, {
        onCompleted: ({ restampBillNotaCredito }: { restampBillNotaCredito: ToBillArguments<ToBillEvent> }) => {
            sharedToasterSubject.next({ type: 'confirm', message: 'Nota de crédito emitida correctamente' });
            history.push({
                pathname: '/',
                state: { billArgument: restampBillNotaCredito }
            })
        },
        onError: (e) => {
            console.error('Error submitting nota');
            setStamping(false)
            sharedToasterSubject.next({ type: 'error', message: parseStampErrorFromGraphQL(e.graphQLErrors[0]), clearTs: 5200, clickToClose: true })
        }
    })

    const [getBillEventsByRfc] = useLazyQuery(graphqlSchema.FISCALPOP.CFDI.getBillsByRfc, {
        onCompleted: ({ getBillsByRfc }: { getBillsByRfc: CFDI[] }) => {
            // Only Active bills can be related
            const parsedBills = getBillsByRfc.filter(bill => bill.status).map(gBBR => {
                return Object.assign({}, gBBR, { json: JSON.parse(gBBR.json) }) as unknown as FiscalpopCFDI;
            })
            console.log('parsedBills: ', parsedBills);
            setBillRelates(parsedBills);
        },
        onError: (e) => {
            console.error('Error getting by name: ', e.graphQLErrors[0])
        }
    })

    useEffect(() => {
        // Edit nota de crédito
        if (eventid) {
            const { billEvents } = location.state as { billEvents: ToBillEvent[] };
            console.log('UE billEvents: ', billEvents);
            dispatchBill({ property: '_replace', value: billEvents[0].NotaCreditoCFDI });
        }
    }, [eventid, location])

    useEffect(() => {
        if (cfdiRelatedOptions.length) {
            const formasPago = cfdiRelatedOptions.map(({ json }) => json['@'].FormaPago);
            console.log('Dispaching Forma pago', formasPago)
            const _formaPago = formasPago[0] === '99' ? '03' : formasPago[0]
            dispatchBill({ property: 'comprobante', value: { formaPago: _formaPago } })
        }
    }, [cfdiRelatedOptions])

    // Handlers ============

    const handleReceptorChange = (property: 'nombre' | 'rfc' | 'usoCFDI' | 'email') => (value: string) => {
        const receptor = Object.assign({}, cfdi.receptor);
        if (property === 'rfc') {
            value = (value || '').toUpperCase();
        }
        if (property === 'email') {
            value = (value || '').toLowerCase();
        }
        receptor[property] = value;
        dispatchBill({ property: 'receptor', value: receptor });
        if (property === 'nombre') {
            searchReceptorByName(value);
        }
        if (property === 'rfc') {
            searchReceptorByRfc(value);
        }
    }

    const handleReceptorAutofill = (receptorAsString: string) => {
        const receptor: Receptor = JSON.parse(receptorAsString);
        console.log('Receptor to add: ', receptor);
        const _receptor = Object.assign({}, cfdi.receptor, { nombre: receptor.name, rfc: receptor.rfc, customerId: receptor._id });
        if (receptor.email) {
            _receptor.email = receptor.email;
        }
        if (receptor.zip) {
            _receptor.zip = receptor.zip;
        }
        if (receptor.regimen) {
            _receptor.regimen = receptor.regimen;
        }
        const comprobante: Bill = Object.assign({}, cfdi);
        comprobante.receptor = _receptor;
        if (receptor.seriePreference) {
            // Pull serie ref
            comprobante.serie = receptor.seriePreference;
        }
        dispatchBill({ property: 'comprobante', value: comprobante });
        getBillEventsByRfc({
            variables: {
                rfc: receptor.rfc
            }
        })
    }

    const handleConceptosChange = (updated: BillConceptos[]) => {
        dispatchBill({ property: 'conceptos', value: updated });
    }

    const onRelatedBillChange = () => {
        if (confirmedRelated) {
            setConfirmRelated(false);
            return;
        }
        const uuids = cfdiRelatedOptions.map(({ uuid }) => uuid);
        console.log(`<onRelatedBillChange> cfdiRelatedOptions: `, cfdiRelatedOptions)
        const _related = cfdi.cfdiRelacionados;
        _related.uuids = uuids;
        dispatchBill({ property: 'cfdiRelacionados', value: _related })
        setConfirmRelated(true);
        // Scroll down to the conceptos, which just became available
        setTimeout(() => {
            const { y } = document.getElementById('_Conceptos_Block')?.getBoundingClientRect();
            if (!!y) {
                document.getElementById('CreditNote').scrollTop = y;
            }
        }, 550)
    }

    const onRelatedBillSelect = (billSelected: FiscalpopCFDI) => (selected: boolean) => {
        console.log('Selected: ', selected);
        dispatchCfdiRelated({ action: selected ? 'select' : 'unselect', value: billSelected })
    }

    const handleRelationType = (tipoRel: string) => {
        const _related = cfdi.cfdiRelacionados;
        _related.tipoRelacion = tipoRel;
        dispatchBill({ property: 'cfdiRelacionados', value: _related })
    }

    const _clearBill = () => {
        dispatchBill({ property: '_replace', value: EMPTY_CFDI() })
        setConfirmRelated(false);
    }

    const _submitNota = () => {
        // Consider some saved receptors might not have ZIP and Regimen, use modal to update Receptor here
        if (!cfdi.receptor.zip || !cfdi.receptor.regimen) {
            setReceptorEditDialog(true);
            return;
        }

        if (!validateNotaCredIsComplete(cfdi)) {
            setValidRequested(true);
            return;
        }
        setStamping(true);
        if (eventid) {
            submitNotaToEdit({
                variables: {
                    replaceNotaId: eventid,
                    cfdi
                }
            })
        } else {
            submitNota({
                variables: {
                    uuidRelated: cfdi.cfdiRelacionados.uuids[0],
                    cfdi
                }
            })
        }
    }

    const handleReceptorEdit = (receptorUpdated: Bill['receptor']) => {
        dispatchBill({ property: 'receptor', value: receptorUpdated });
        setReceptorEditDialog(false);
    }

    const onCloseDialog = () => {
        setReceptorEditDialog(false);
    }

    const { total } = calculateTotal(cfdi.conceptos);
    const relatedTotal = cfdiRelatedOptions.reduce((p, c) => p += parseFloat(c.json['@'].Total), 0);
    const totalOverflow = !cfdiRelatedOptions.length ? false : (relatedTotal < total);

    console.log('CFDI ', cfdi);
    return (
        <div id="CreditNote">
            <SectionTitleBar currentUser={currentUser} title={!!eventid ? `Editar Nota de crédito` : `Emitir Nota de crédito`} />
            <div className="facturarContent">
                <CreditNoteRelated
                    cfdi={cfdi}
                    confirmedRelated={confirmedRelated}
                    billRelates={billRelates}
                    cfdiRelatedOptions={cfdiRelatedOptions}
                    receptorOptions={receptorOptions}
                    relatedTotal={relatedTotal}
                    onReceptorChange={handleReceptorChange}
                    onReceptorAutofill={handleReceptorAutofill}
                    onRelationType={handleRelationType}
                    onRelatedBillSelect={onRelatedBillSelect}
                    onRelatedBillChange={onRelatedBillChange}
                />
                {
                    confirmedRelated ?
                        (
                            <>
                                <FacturarConceptosDevolucion cfdi={cfdi} onCfdiChange={handleConceptosChange} validRequested={validRequested} authToken={currentUser.professionalProfile.authToken} />
                                <FacturaTotales cfdi={cfdi} />
                                <CreditNoteActions
                                    stamping={stamping}
                                    validRequested={validRequested}
                                    totalOverflow={totalOverflow}
                                    relatedTotal={relatedTotal}
                                    total={total}
                                    onClear={_clearBill}
                                    onSubmit={_submitNota}
                                />
                            </>
                        ) :
                        null
                }

            </div>
            <CreditNotesClientDialog
                isOpen={receptorEditDialog}
                receptor={cfdi.receptor}
                onRequestClose={onCloseDialog}
                onClientSubmit={handleReceptorEdit}
            />
        </div>
    )
}

export default CreditNote;
